import { Trans, useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Box from "@material-ui/core/Box";
import PostageDeliveryOption from "./PostageDeliveryOption";
import Icon from "@material-ui/core/Icon";
import IconButton from "@material-ui/core/IconButton";
import Alert from "@material-ui/lab/Alert";
import SchoolDeliveryOptions from "./SchoolDeliveryOptions";
import {
  CheckoutCard,
  CheckoutCardList,
  CheckoutCardTitle,
} from "./CheckoutCard";
import ListItemRadioButton from "../../../components/Common/ListItemRadioButton";
import { formatCurrency } from "../../../utils";
import { useCheckout } from "../../providers/CheckoutProvider";
import PickupDeliveryOptions from "./PickupDeliveryOptions";
import DigitalDeliveryOption from "./DigitalDeliveryOption";
import { Skeleton } from "@material-ui/lab";
import useIsMobileDevice from "../../../hooks/useIsMobileDevice";
import useTheme from "@material-ui/core/styles/useTheme";
import Address from "../Address";

const useStyles = makeStyles((theme) => ({
  deliveryMethodHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2),
    "& > svg": {
      marginRight: theme.spacing(2),
    },
  },
  selected: {
    backgroundColor: "red",
    "&.MuiListItem-root, &.MuiListItem-root:active, &.MuiListItem-root:hover":
      {
        backgroundColor: theme.palette.secondary.light,
      },
  },
  deliveryMethodName: {
    flexGrow: 1,
  },
  deliveryPrice: {
    whiteSpace: "nowrap",
    fontWeight: 700,
    minWidth: "80px",
    textAlign: "right",
  },
  secondaryInfo: {
    fontSize: "0.8rem",
    color: "#777",
  },
  deliveryAddress: {
    fontStyle: "normal",
    "& .address--address": {
      fontSize: "0.8rem",
      color: "#777",
    },
  },
  selectedDeliveryOption: {
    display: "flex",
    alignItems: "center",
  },
  primaryInfoContainer: {
    flexGrow: 1,
    fontSize: "13px",
  },
  deliveryInfoHeader: {
    fontSize: "15px",
    fontWeight: 500,
  },
  skeletonItem: {
    display: "flex",
    marginBottom: theme.spacing(3),
  },
  skeletonRadioButtonContainer: {
    padding: theme.spacing(2),
    width: "40px",
    height: "40px",
  },
  skeletonTexts: {
    flexGrow: 1,
    marginRight: theme.spacing(2),
  },
  skeletonPrice: {
    display: "flex",
    alignItems: "center",
    flexBasis: "60px",
    "& > span": {
      flexGrow: 1,
    },
  },
  actionButtonContainer: {
    paddingRight: theme.clientType == "desktop" ? 0 : theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      paddingRight: theme.spacing(1),
    },
  },
  catalogueDeliveryInfo: {
    marginTop: "-10px",
    paddingRight: theme.clientType == "desktop" ? 0 : theme.spacing(3),
    paddingLeft: theme.clientType == "desktop" ? 0 : theme.spacing(3),
    marginBottom: theme.spacing(2),
    "& .MuiAlert-message": {
      fontSize: "12px",
    },
  },
}));

function getDeliveryMethodPrice(deliveryMethod) {
  let deliveryOptionsList = deliveryMethod.pickupLocations;
  if (deliveryMethod.deliveryOptions) {
    deliveryOptionsList = deliveryMethod.deliveryOptions;
  }
  console.log("Calculating price for", deliveryMethod, deliveryOptionsList);
  return deliveryOptionsList.reduce((maxPrice, deliveryOption) => {
    const delOptPrice = parseFloat(deliveryOption.price);

    // Skip express shipping:
    if (deliveryOption.uuid && deliveryOption.uuid.endsWith("-111111111111")) {
      return maxPrice;
    }
    if (delOptPrice > maxPrice) {
      maxPrice = delOptPrice;
    }
    return maxPrice;
  }, 0);
}

export default () => {
  const {
    deliveryMethods,
    setDeliveryOption: checkoutSetDeliveryOption,
    deliveryOption,
    deliveryOptionData,
    setCurrentStep,
    currentStep,
  } = useCheckout();
  const [deliveryMethod, setDeliveryMethod] = useState(null);
  const isMobileDevice = useIsMobileDevice();

  useEffect(() => {
    if (deliveryMethods?.length == 1) {
      setDeliveryMethod(deliveryMethods[0]);
    }
    // If we change due to catalogue or something?
    else if (deliveryMethods?.length > 1) {
      setDeliveryMethod(null);
    }
  }, [deliveryMethods]);

  console.log("Delivery methods", deliveryMethods);

  const handleEdit = () => {
    setCurrentStep("select-delivery-option");
  };

  const handleDeliveryMethod = (deliveryMethod) => {
    setDeliveryMethod(deliveryMethod);
  };

  console.log("The current step is", currentStep);

  const edit = currentStep == "select-delivery-option";

  return (
    <CheckoutCard>
      <Box
        display="flex"
        flexDirection={edit || isMobileDevice ? "column" : "row"}
      >
        <CheckoutCardTitle active={edit} complete={!edit}>
          <Trans>1. Delivery</Trans>
        </CheckoutCardTitle>
        {edit ? (
          <SelectDeliveryMethod
            handleDeliveryMethod={handleDeliveryMethod}
            deliveryMethod={deliveryMethod}
          />
        ) : (
          <SelectedDeliveryOption
            deliveryMethod={deliveryMethod}
            deliveryOption={deliveryOption}
            deliveryOptionData={deliveryOptionData}
            handleEdit={handleEdit}
          />
        )}
      </Box>
    </CheckoutCard>
  );
};

const SelectDeliveryMethod = ({ deliveryMethod, handleDeliveryMethod }) => {
  const { catalogueMaxDeliveryTime } = useCheckout();
  const classes = useStyles();
  return (
    <>
      <SelectMethod
        deliveryMethod={deliveryMethod}
        handleChange={handleDeliveryMethod}
      />
      {catalogueMaxDeliveryTime?.days && (
        <div className={classes.catalogueDeliveryInfo}>
          <Alert severity="info">
            <Trans>
              Catalogues are shipped separately, normally within{" "}
              {{ days: catalogueMaxDeliveryTime.days }} days after the last
              photo day.
            </Trans>
          </Alert>
        </div>
      )}
      {deliveryMethod && <Options deliveryMethod={deliveryMethod} />}
    </>
  );
};

const SelectMethod = ({
  handleChange,
  deliveryMethod: currentDeliveryMethod,
}) => {
  const { deliveryMethods } = useCheckout();
  const { t } = useTranslation();
  const classes = useStyles();

  if (!deliveryMethods) {
    return <DeliveryMethodsSkeleton />;
  }

  return (
    <>
      <CheckoutCardList>
        {deliveryMethods.map((deliveryMethod, idx) => {
          let description = "";
          switch (deliveryMethod.type) {
            case "store-pickup":
              let storeDays =
                deliveryMethod.pickupLocations[0].deliveryTime.string;
              description = t("Pick up your order in our store in %s", {
                postProcess: "sprintf",
                sprintf: [storeDays],
              });
              break;
            case "postage":
              let days = parseInt(
                deliveryMethod.deliveryOptions[0].deliverytime,
              );
              if (days > 1) {
                description = t(
                  "The order will be shipped within %d business days",
                  {
                    postProcess: "sprintf",
                    sprintf: [days],
                  },
                );
              } else {
                description = t(
                  "The order will be shipped within %d business day",
                  {
                    postProcess: "sprintf",
                    sprintf: [days],
                  },
                );
              }
              break;
            case "school-pickup":
              description = t(
                "We will deliver your order directly to the school/club",
              );
              break;
            case "digital-delivery":
              description = t(
                "The digital order content will be available instantly after purchase",
              );
              break;
          }

          const price = getDeliveryMethodPrice(deliveryMethod);
          const priceString = price == 0 ? t("Free") : formatCurrency(price);

          const selected =
            currentDeliveryMethod &&
            currentDeliveryMethod.type === deliveryMethod.type;

          return (
            <ListItemRadioButton
              key={deliveryMethod.name}
              labelId={"dm_" + deliveryMethod.name.replace(/\s/g, "_")}
              selected={selected}
              onClick={() => handleChange(deliveryMethod)}
              primaryText={deliveryMethod.name}
              secondaryText={description}
              action={
                <Box className={classes.deliveryPrice}>{priceString}</Box>
              }
            />
          );
        })}
      </CheckoutCardList>
    </>
  );
};

const DeliveryMethodsSkeleton = () => {
  const classes = useStyles();

  return [...Array(3).keys()].map((idx) => {
    return (
      <div className={classes.skeletonItem}>
        <div className={classes.skeletonRadioButtonContainer}>
          <Skeleton animation="pulse" variant="circle" />
        </div>
        <div className={classes.skeletonTexts}>
          <Skeleton animation="pulse" variant="text" />
          <Skeleton animation="pulse" variant="text" />
        </div>
        <div className={classes.skeletonPrice}>
          <Skeleton animation="pulse" variant="text" />
        </div>
      </div>
    );
  });
};

const SelectedDeliveryOption = ({
  deliveryOption,
  deliveryMethod,
  deliveryOptionData,
  handleEdit,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobileDevice = useIsMobileDevice();
  console.log("Current delivery option", deliveryOption, deliveryMethod);
  const price = deliveryOption.price;
  const priceString = price == 0 ? t("Free") : formatCurrency(price);

  let deliveryInfo = null;

  if (deliveryMethod.type == "school-pickup") {
    deliveryInfo = (
      <div>
        <div className={classes.deliveryInfoHeader}>
          {t("School/club delivery (%s)", {
            postProcess: "sprintf",
            sprintf: [priceString],
          })}
        </div>
        <div>
          {deliveryOptionData.subject.group}: {deliveryOptionData.subject.name}
        </div>
        <div className={classes.secondaryInfo}>
          {deliveryOptionData.contactInfo.name}
        </div>
        <div className={classes.secondaryInfo}>
          {deliveryOptionData.contactInfo.emailAddress}
        </div>
      </div>
    );
  } else if (deliveryMethod.type == "store-pickup") {
    deliveryInfo = (
      <div>
        <div className={classes.deliveryInfoHeader}>
          {t("Store pickup (%s)", {
            postProcess: "sprintf",
            sprintf: [priceString],
          })}
        </div>
        <div>{deliveryOption.description}</div>
        <div className={classes.secondaryInfo}>
          {deliveryOption.pickupaddress1}
        </div>
        <div className={classes.secondaryInfo}>
          {deliveryOption.pickuppostalcode} {deliveryOption.pickuppostalcity}
        </div>
      </div>
    );
  } else if (deliveryMethod.type == "postage") {
    const { deliveryAddress } = deliveryOptionData;
    const address = {
      name: deliveryAddress.name,
      address1: deliveryAddress.address,
      address2: deliveryAddress.address2,
      postalCode: deliveryAddress.postalCode,
      city: deliveryAddress.city,
      state: deliveryAddress.state,
    };
    deliveryInfo = (
      <div>
        <div className={classes.deliveryInfoHeader}>
          {deliveryOption.isExpress
            ? t("Ship by mail - Express (%s)", {
                postProcess: "sprintf",
                sprintf: [priceString],
              })
            : t("Ship by mail (%s)", {
                postProcess: "sprintf",
                sprintf: [priceString],
              })}
        </div>

        <Address address={address} className={classes.deliveryAddress} />
      </div>
    );
  } else if (deliveryMethod.type == "digital-delivery") {
    const { contactInfo } = deliveryOptionData;
    deliveryInfo = (
      <div>
        <div className={classes.deliveryInfoHeader}>
          {t("Digital delivery (%s)", {
            postProcess: "sprintf",
            sprintf: [priceString],
          })}
        </div>
        <div>{contactInfo.emailAddress}</div>
        <div className={classes.secondaryInfo}>{contactInfo.name}</div>
      </div>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      flexGrow={1}
      pb={isMobileDevice ? 2 : 0}
    >
      <Box
        px={3}
        py={theme.clientType != "desktop" && !isMobileDevice ? 2 : 0}
      >
        {deliveryInfo}
      </Box>
      <Box
        display="flex"
        alignItems="center"
        className={classes.actionButtonContainer}
      >
        <IconButton
          onClick={handleEdit}
          color="primary"
          aria-label={t("Edit delivery")}
        >
          <Icon>edit</Icon>
        </IconButton>
      </Box>
    </Box>
  );
};

const Options = ({ deliveryMethod, handleChange }) => {
  let MethodOptions = SchoolDeliveryOptions;
  switch (deliveryMethod.type) {
    case "store-pickup":
      MethodOptions = PickupDeliveryOptions;
      break;
    case "postage":
      MethodOptions = PostageDeliveryOption;
      break;
    case "digital-delivery":
      MethodOptions = DigitalDeliveryOption;
      break;
  }

  console.log("Type", deliveryMethod.type, MethodOptions);
  return (
    <MethodOptions
      deliveryMethod={deliveryMethod}
      handleChange={handleChange}
    />
  );
};
